import { useCallback, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { useAIAction } from '../../hooks/useAIAction';
import { VoiceLibraryBrowser } from '../voices/VoiceLibraryBrowser';
import type { ParagraphDto } from '../../api/queries/books';

interface ChapterNarrationDialogProps {
  open: boolean;
  onClose: () => void;
  bookId: string;
  chapterId: string;
  paragraphs: ParagraphDto[];
}

type ParagraphStatus = 'pending' | 'narrating' | 'done' | 'error';

interface ParagraphProgress {
  id: string;
  status: ParagraphStatus;
  error?: string;
}

const CREDITS_PER_PARAGRAPH = 2;

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

export function ChapterNarrationDialog({
  open,
  onClose,
  bookId,
  chapterId,
  paragraphs,
}: ChapterNarrationDialogProps) {
  const [voiceBrowserOpen, setVoiceBrowserOpen] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [selectedVoiceName, setSelectedVoiceName] = useState('');
  const [skipExisting, setSkipExisting] = useState(true);
  const [pauseDuration, setPauseDuration] = useState('1');
  const [speed, setSpeed] = useState(1.0);
  const [narrating, setNarrating] = useState(false);
  const [progress, setProgress] = useState<ParagraphProgress[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const cancelledRef = useRef(false);

  const generateAudio = useAIAction('audio-generation');

  // For now we consider any paragraph with text content as eligible.
  // "Existing audio" check is a placeholder — the ParagraphDto doesn't expose
  // an `audioUrl` flag yet, so we narrate all by default when skipExisting is
  // unchecked.
  const eligibleParagraphs = paragraphs.filter((p) => {
    const text = stripHtml(p.contentHtml);
    return text.length > 0;
  });

  const toNarrateCount = eligibleParagraphs.length;
  const estimatedCredits = toNarrateCount * CREDITS_PER_PARAGRAPH;

  const handleSelectVoice = useCallback(
    (voiceId: string, voiceName: string) => {
      setSelectedVoiceId(voiceId);
      setSelectedVoiceName(voiceName);
    },
    [],
  );

  const handleNarrate = async () => {
    cancelledRef.current = false;
    setNarrating(true);
    const initial: ParagraphProgress[] = eligibleParagraphs.map((p) => ({
      id: p.id,
      status: 'pending',
    }));
    setProgress(initial);
    setCurrentIndex(0);

    for (let i = 0; i < eligibleParagraphs.length; i++) {
      if (cancelledRef.current) break;
      const p = eligibleParagraphs[i]!;
      setCurrentIndex(i);
      setProgress((prev) =>
        prev.map((item) =>
          item.id === p.id ? { ...item, status: 'narrating' } : item,
        ),
      );

      try {
        await generateAudio.mutateAsync({
          text: stripHtml(p.contentHtml),
          voice_id: selectedVoiceId || undefined,
          stability: 0.75,
          speed,
          paragraph_id: p.id,
          book_id: bookId,
          chapter_id: chapterId,
        });
        setProgress((prev) =>
          prev.map((item) =>
            item.id === p.id ? { ...item, status: 'done' } : item,
          ),
        );
      } catch (err) {
        setProgress((prev) =>
          prev.map((item) =>
            item.id === p.id
              ? { ...item, status: 'error', error: (err as Error).message }
              : item,
          ),
        );
      }
    }

    setNarrating(false);
    setSnackMessage('Chapter narration complete');
    setSnackOpen(true);
  };

  const handleCancel = () => {
    if (narrating) {
      cancelledRef.current = true;
      setNarrating(false);
    }
    onClose();
  };

  const completedCount = progress.filter((p) => p.status === 'done').length;
  const errorCount = progress.filter((p) => p.status === 'error').length;
  const progressPercent =
    progress.length > 0
      ? ((completedCount + errorCount) / progress.length) * 100
      : 0;

  // Prevent TS unused warnings for informational props
  void skipExisting;
  void pauseDuration;

  return (
    <>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle>Narrate Chapter</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {/* Voice selector */}
            <Stack direction="row" spacing={1} alignItems="center">
              <RecordVoiceOverIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                Narrator voice:{' '}
                <strong>{selectedVoiceName || 'Default narrator'}</strong>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setVoiceBrowserOpen(true)}
              >
                Choose voice
              </Button>
            </Stack>

            {/* Options */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipExisting}
                  onChange={(e) => setSkipExisting(e.target.checked)}
                />
              }
              label="Skip paragraphs with existing audio"
            />

            <FilterableSelect
              options={[
                { value: '0.5', label: '0.5s' },
                { value: '1', label: '1s' },
                { value: '2', label: '2s' },
              ]}
              value={pauseDuration}
              onChange={(newValue) => setPauseDuration(newValue)}
              label="Pause between paragraphs"
              allowEmpty={false}
              size="small"
              helperText="Actual stitching is handled on the backend"
            />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Speed: {speed.toFixed(2)}x
              </Typography>
              <Slider
                min={0.75}
                max={1.5}
                step={0.05}
                value={speed}
                onChange={(_, v) => setSpeed(v as number)}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* Summary */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                label={`Will narrate ${toNarrateCount} of ${paragraphs.length} paragraphs`}
                size="small"
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                Estimated cost: {estimatedCredits} credits (
                {CREDITS_PER_PARAGRAPH} credits per paragraph)
              </Typography>
            </Stack>

            {/* Progress section (visible after clicking Narrate) */}
            {progress.length > 0 && (
              <Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{ flex: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(progressPercent)}%
                  </Typography>
                </Stack>

                {narrating && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Narrating {currentIndex + 1} of {progress.length}...
                  </Typography>
                )}

                {!narrating && completedCount > 0 && (
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ mb: 1 }}
                  >
                    Completed {completedCount} of {progress.length}
                    {errorCount > 0 ? ` (${errorCount} failed)` : ''}
                  </Typography>
                )}

                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {progress.map((item, idx) => (
                    <ListItem key={item.id} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {item.status === 'done' && (
                          <CheckCircleIcon fontSize="small" color="success" />
                        )}
                        {item.status === 'error' && (
                          <ErrorIcon fontSize="small" color="error" />
                        )}
                        {item.status === 'narrating' && (
                          <HourglassEmptyIcon
                            fontSize="small"
                            color="primary"
                          />
                        )}
                        {item.status === 'pending' && (
                          <RadioButtonUncheckedIcon
                            fontSize="small"
                            color="disabled"
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={`Paragraph ${idx + 1}`}
                        secondary={
                          item.status === 'error' ? item.error : undefined
                        }
                        primaryTypographyProps={{ variant: 'caption' }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          color: 'error',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>
            {narrating ? 'Cancel' : 'Close'}
          </Button>
          <Button
            variant="contained"
            onClick={handleNarrate}
            disabled={narrating || toNarrateCount === 0}
          >
            {narrating ? 'Narrating...' : 'Narrate'}
          </Button>
        </DialogActions>
      </Dialog>

      <VoiceLibraryBrowser
        open={voiceBrowserOpen}
        onClose={() => setVoiceBrowserOpen(false)}
        onSelectVoice={handleSelectVoice}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        message={snackMessage}
      />
    </>
  );
}
